import React, { useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import { EffectCoverflow, Navigation } from "swiper/modules";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import { useNavigate } from "react-router-dom";
import {
  Building2, // Builder Company
  User, // Builder Name
  BadgeCheck, // Ducktail ID
  Phone, // Contact
  Mail, // Email
  ClipboardList, // Project Name
  MapPin, // Location
  CalendarDays, // Dates
  IndianRupee, // Budget
  Activity, // Status
  MessageCircle, // Chat Icon
  CreditCard, // Payment Icon (Alternative)
  FileText, // Document Icon
} from "lucide-react";

// Helper function to capitalize the first letter
const capitalize = (s) => (s && s[0].toUpperCase() + s.slice(1)) || "";

const LiveCard = () => {
  const { projects, fetchProjects, isLoading, error } = useProjectStatus();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects("active");
  }, [fetchProjects]);

  const handlenavigate = (path) => {
    navigate(path);
  };

  // Function to format currency
  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return "N/A";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  if (error) {
    return <p className="text-center text-red-500">{error}</p>;
  }

  return (
    <div className="max-w-3xl mx-auto relative py-4 px-2 md:px-0">
      {" "}
      {/* Increased max-width */}
      {isLoading ? (
        <p className="text-center text-gray-500 text-sm py-10">
          Loading projects...
        </p>
      ) : projects.length === 0 ? (
        <p className="text-center text-gray-500 text-sm py-10">
          No live projects found.
        </p>
      ) : (
        <Swiper
          effect={"coverflow"}
          grabCursor={true}
          centeredSlides={true}
          slidesPerView={1} // Show one full card clearly
          breakpoints={{
            // Adjust breakpoints if needed, maybe show slightly more on larger screens
            768: { slidesPerView: 1.5, spaceBetween: -40 },
            1024: { slidesPerView: 2, spaceBetween: -60 },
          }}
          spaceBetween={-20} // Adjust overlap
          coverflowEffect={{
            rotate: 0,
            stretch: 30, // Less stretch
            depth: 100, // More depth for perspective
            modifier: 1.5, // Adjust modifier
            slideShadows: false, // Optionally disable shadows if preferred
          }}
          navigation={{
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          }}
          modules={[EffectCoverflow, Navigation]}
          className="w-full !py-4" // Added padding for better visual spacing
        >
          {projects.map((project) => (
            <SwiperSlide key={project.id} className="!h-auto">
              <div className="bg-gradient-to-br from-white to-blue-50 shadow-xl rounded-xl border border-gray-200 p-4 flex flex-col h-full min-h-[400px]">
                {" "}
                {/* Min height for consistency */}
                {/* Builder Info Section */}
                <div className="mb-4 pb-3 border-b border-gray-200">
                  <div className="flex items-center mb-2 gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <h2
                      className="text-base font-semibold text-gray-800 truncate flex-1"
                      title={project.builder_company}
                    >
                      {project.builder_company || "Unknown Builder"}
                    </h2>
                  </div>
                  {project.builder_name && (
                    <div className="flex items-center text-xs text-gray-500 mb-2 gap-3">
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span>{project.builder_name}</span>
                    </div>
                  )}
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600">
                        ID: {project.builder_ducktail_id || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600">
                        {project.builder_contact || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span
                        className="text-gray-600 truncate"
                        title={project.builder_email}
                      >
                        {project.builder_email || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Project Info Section */}
                <div className="mb-4 pb-3 border-b border-gray-200">
                  <div className="flex items-center gap-2 mb-1.5">
                    <ClipboardList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span
                      className="text-xs font-medium text-gray-700 truncate"
                      title={project.project_name}
                    >
                      {project.project_name || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate" title={project.project_location}>
                      {project.project_location || "N/A"}
                    </span>
                  </div>
                </div>
                {/* Dates & Budget Section */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-xs">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div>
                      <div className="text-gray-500">Start Date</div>
                      <div className="font-medium text-gray-700">
                        {formatDate(project.starting_date)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <div>
                      <div className="text-gray-500">Deadline</div>
                      <div className="font-medium text-gray-700">
                        {formatDate(project.deadline_date)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <IndianRupee className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div>
                      <div className="text-gray-500">Budget</div>
                      <div className="font-medium text-gray-700">
                        {formatCurrency(project.project_budget)}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Status Section */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-xs">
                    <Activity className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-500">Status:</span>
                    <span
                      className={`font-semibold capitalize px-2 py-0.5 rounded-full text-white ${
                        project.status === "active"
                          ? "bg-green-500"
                          : "bg-gray-500"
                      }`}
                    >
                      {capitalize(project.status) || "N/A"}
                    </span>
                  </div>
                </div>
                {/* Action Buttons - Pushed to bottom */}
                <div className="mt-auto flex justify-around items-center pt-3 border-t border-gray-200">
                  <button
                    className="flex flex-col items-center text-gray-600 hover:text-blue-600 transition-colors text-xs"
                    onClick={() => handlenavigate("/customer/chatbox")}
                  >
                    <MessageCircle className="w-5 h-5 mb-0.5" />
                    <span>Chat</span>
                  </button>
                  <button
                    className="flex flex-col items-center text-gray-600 hover:text-blue-600 transition-colors text-xs"
                    onClick={() => handlenavigate("/customer/paymenthistory")}
                  >
                    <CreditCard className="w-5 h-5 mb-0.5" />
                    <span>Payment</span>
                  </button>
                  <button
                    className="flex flex-col items-center text-gray-600 hover:text-blue-600 transition-colors text-xs"
                    onClick={() => handlenavigate("/customer/requestcard")} // Assuming docs relate to completion requests for now
                  >
                    <FileText className="w-5 h-5 mb-0.5" />
                    <span>Docs</span>
                  </button>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      {/* Keep the floating support button */}
      <button
        className="fixed bottom-3 right-3 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg z-20" // Ensure button is above swiper if needed
        onClick={() => navigate("/customer/help")}
      >
        <img
          src="/assets/Support.png"
          alt="Icon"
          className="w-7 h-7 sm:w-9 sm:h-9"
        />
      </button>
    </div>
  );
};

export default LiveCard;
